// MediaLoader
// -----------
// Handles:
//   - Directory picking
//   - Recursive scanning (optional)
//   - Filtering for image + video MIME types
//   - Creating object URLs
//   - Returning a clean array of { name, url, file, type }

export class MediaLoader {
  constructor() {
    this.acceptedImageTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "image/bmp",
    ];

    this.acceptedVideoTypes = [
      "video/mp4",
      "video/webm",
      "video/ogg",
      "video/quicktime",
    ];
  }

  async pickDirectory() {
    if (!window.showDirectoryPicker) {
      throw new Error("Directory picker API not supported in this browser.");
    }

    return await window.showDirectoryPicker({ mode: "read" });
  }

  async loadFromDirectory(
    dirHandle,
    includeSubdirs = false,
    onFileFound = null,
  ) {
    try {
      const out = [];
      await this._scanDirectory(
        dirHandle,
        out,
        includeSubdirs,
        "",
        onFileFound,
      );
      return out;
    } catch (err) {
      console.error("Error loading directory:", err);
      return [];
    }
  }

  async _scanDirectory(
    dirHandle,
    out,
    includeSubdirs,
    pathPrefix = "",
    onFileFound = null,
  ) {
    // 1. Android requires explicit permission
    let perm = await dirHandle.queryPermission({ mode: "read" });
    if (perm !== "granted") {
      perm = await dirHandle.requestPermission({ mode: "read" });
      if (perm !== "granted") {
        console.warn("Permission denied for directory:", dirHandle.name);
        return results;
      }
    }

    for await (const entry of dirHandle.values()) {
      if (entry.kind === "file") {
        const file = await entry.getFile();

        let type = null;
        if (this.acceptedImageTypes.includes(file.type)) type = "image";
        if (this.acceptedVideoTypes.includes(file.type)) type = "video";

        if (type == null) {
          console.warn("Unsupported file type:", file.name, file.type);
        }

        if (type && !file.name.startsWith(".")) {
          if (onFileFound) onFileFound();
          out.push({
            name: pathPrefix + file.name,
            file,
            url: URL.createObjectURL(file),
            type,
          });
        }
      }

      if (entry.kind === "directory" && includeSubdirs) {
        await this._scanDirectory(
          entry,
          out,
          includeSubdirs,
          pathPrefix + entry.name + "/",
          onFileFound,
        );
      }
    }
  }
}
